import type { User, Transaction, ChatRoom, ChatMessage, AdminAction, KycDocument, AdminUser, Notification, BankAccount, Card, Loan, LoanPayment, ScheduledTransfer, BillPayment } from '@/types';
import { useStore } from '@/store';

// Mock Bank Accounts
export const mockBankAccounts: BankAccount[] = [
  {
    id: 'acc_1',
    userId: 'u1',
    type: 'checking',
    name: 'Primary Checking',
    accountNumber: '1234567890',
    routingNumber: '021000021',
    balance: 26887.09,
    currency: 'USD',
    status: 'active',
    interestRate: 0.5,
    createdAt: '2024-01-10T08:00:00Z',
    updatedAt: '2024-12-15T14:30:00Z',
    isPrimary: true,
    color: 'mint',
  },
  {
    id: 'acc_2',
    userId: 'u1',
    type: 'savings',
    name: 'Emergency Fund',
    accountNumber: '2345678901',
    routingNumber: '021000021',
    balance: 15000.00,
    currency: 'USD',
    status: 'active',
    interestRate: 4.5,
    createdAt: '2024-02-15T10:00:00Z',
    updatedAt: '2024-12-10T09:00:00Z',
    isPrimary: false,
    color: 'purple',
  },
  {
    id: 'acc_3',
    userId: 'u1',
    type: 'savings',
    name: 'Vacation Savings',
    accountNumber: '3456789012',
    routingNumber: '021000021',
    balance: 5200.00,
    currency: 'USD',
    status: 'active',
    interestRate: 4.5,
    createdAt: '2024-03-20T14:00:00Z',
    updatedAt: '2024-11-28T11:00:00Z',
    isPrimary: false,
    color: 'gold',
  },
  {
    id: 'acc_4',
    userId: 'u2',
    type: 'checking',
    name: 'Main Checking',
    accountNumber: '4567890123',
    routingNumber: '021000021',
    balance: 12450.75,
    currency: 'USD',
    status: 'active',
    interestRate: 0.5,
    createdAt: '2024-02-05T10:30:00Z',
    updatedAt: '2024-12-18T09:45:00Z',
    isPrimary: true,
    color: 'navy',
  },
];

// Mock Cards
export const mockCards: Card[] = [
  {
    id: 'card_1',
    userId: 'u1',
    type: 'debit',
    name: 'Sarah Chen',
    lastFourDigits: '4532',
    expiryMonth: 12,
    expiryYear: 2027,
    cvv: '123',
    status: 'active',
    balance: 2150.75,
    dailyLimit: 5000,
    monthlyLimit: 25000,
    isVirtual: false,
    cardNetwork: 'visa',
    color: 'mint',
    createdAt: '2024-01-10T08:00:00Z',
    updatedAt: '2024-12-15T14:30:00Z',
  },
  {
    id: 'card_2',
    userId: 'u1',
    type: 'credit',
    name: 'Sarah Chen',
    lastFourDigits: '8901',
    expiryMonth: 8,
    expiryYear: 2026,
    cvv: '456',
    status: 'active',
    balance: 3450.00,
    creditLimit: 15000,
    availableCredit: 11550,
    dailyLimit: 3000,
    monthlyLimit: 10000,
    isVirtual: false,
    cardNetwork: 'mastercard',
    color: 'purple',
    createdAt: '2024-01-10T08:00:00Z',
    updatedAt: '2024-12-15T14:30:00Z',
  },
  {
    id: 'card_3',
    userId: 'u1',
    type: 'debit',
    name: 'Sarah Chen',
    lastFourDigits: '1234',
    expiryMonth: 6,
    expiryYear: 2027,
    cvv: '789',
    status: 'frozen',
    balance: 0,
    dailyLimit: 1000,
    monthlyLimit: 5000,
    isVirtual: true,
    cardNetwork: 'visa',
    color: 'gold',
    createdAt: '2024-06-15T12:00:00Z',
    updatedAt: '2024-12-01T10:00:00Z',
  },
  {
    id: 'card_4',
    userId: 'u2',
    type: 'debit',
    name: 'James Wilson',
    lastFourDigits: '5678',
    expiryMonth: 3,
    expiryYear: 2028,
    cvv: '234',
    status: 'active',
    balance: 890.25,
    dailyLimit: 3000,
    monthlyLimit: 15000,
    isVirtual: false,
    cardNetwork: 'visa',
    color: 'navy',
    createdAt: '2024-02-05T10:30:00Z',
    updatedAt: '2024-12-18T09:45:00Z',
  },
];

// Mock Loans
export const mockLoans: Loan[] = [
  {
    id: 'loan_1',
    userId: 'u1',
    type: 'personal',
    name: 'Personal Loan',
    principal: 25000,
    interestRate: 8.5,
    termMonths: 36,
    monthlyPayment: 793.25,
    remainingBalance: 18500.75,
    totalPaid: 6500.00,
    status: 'active',
    startDate: '2024-06-01T00:00:00Z',
    endDate: '2027-05-31T00:00:00Z',
    nextPaymentDate: '2025-01-01T00:00:00Z',
    nextPaymentAmount: 793.25,
    createdAt: '2024-05-15T10:00:00Z',
    updatedAt: '2024-12-01T00:00:00Z',
  },
  {
    id: 'loan_2',
    userId: 'u1',
    type: 'student',
    name: 'Student Loan',
    principal: 35000,
    interestRate: 3.8,
    termMonths: 120,
    monthlyPayment: 350.00,
    remainingBalance: 28000.00,
    totalPaid: 7000.00,
    status: 'active',
    startDate: '2022-09-01T00:00:00Z',
    endDate: '2032-08-31T00:00:00Z',
    nextPaymentDate: '2025-01-15T00:00:00Z',
    nextPaymentAmount: 350.00,
    createdAt: '2022-08-15T10:00:00Z',
    updatedAt: '2024-12-01T00:00:00Z',
  },
  {
    id: 'loan_3',
    userId: 'u2',
    type: 'auto',
    name: 'Auto Loan',
    principal: 28000,
    interestRate: 5.9,
    termMonths: 60,
    monthlyPayment: 540.00,
    remainingBalance: 22000.00,
    totalPaid: 6000.00,
    status: 'active',
    startDate: '2024-01-15T00:00:00Z',
    endDate: '2029-01-14T00:00:00Z',
    nextPaymentDate: '2025-01-15T00:00:00Z',
    nextPaymentAmount: 540.00,
    createdAt: '2024-01-01T10:00:00Z',
    updatedAt: '2024-12-01T00:00:00Z',
  },
  {
    id: 'loan_4',
    userId: 'u6',
    type: 'home',
    name: 'Home Mortgage',
    principal: 450000,
    interestRate: 4.2,
    termMonths: 360,
    monthlyPayment: 2200.00,
    remainingBalance: 420000.00,
    totalPaid: 30000.00,
    status: 'active',
    startDate: '2023-06-01T00:00:00Z',
    endDate: '2053-05-31T00:00:00Z',
    nextPaymentDate: '2025-01-01T00:00:00Z',
    nextPaymentAmount: 2200.00,
    collateral: '123 Innovation Dr, San Jose, CA 95110',
    createdAt: '2023-05-15T10:00:00Z',
    updatedAt: '2024-12-01T00:00:00Z',
  },
];

// Mock Loan Payments
export const mockLoanPayments: LoanPayment[] = [
  {
    id: 'lp_1',
    loanId: 'loan_1',
    userId: 'u1',
    amount: 793.25,
    principalPaid: 605.50,
    interestPaid: 187.75,
    remainingBalance: 18500.75,
    status: 'completed',
    dueDate: '2024-12-01T00:00:00Z',
    paidAt: '2024-11-28T14:30:00Z',
    createdAt: '2024-11-28T14:30:00Z',
  },
  {
    id: 'lp_2',
    loanId: 'loan_1',
    userId: 'u1',
    amount: 793.25,
    principalPaid: 610.00,
    interestPaid: 183.25,
    remainingBalance: 17890.75,
    status: 'pending',
    dueDate: '2025-01-01T00:00:00Z',
    createdAt: '2024-12-15T00:00:00Z',
  },
];

// Mock Scheduled Transfers
export const mockScheduledTransfers: ScheduledTransfer[] = [
  {
    id: 'st_1',
    userId: 'u1',
    fromAccountId: 'acc_1',
    toAccountNumber: '1234567890',
    toAccountName: 'Savings Transfer',
    amount: 500,
    currency: 'USD',
    frequency: 'monthly',
    nextRunDate: '2025-01-01T00:00:00Z',
    endDate: '2026-12-31T00:00:00Z',
    status: 'active',
    description: 'Monthly savings contribution',
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'st_2',
    userId: 'u1',
    fromAccountId: 'acc_1',
    toAccountNumber: '9876543210',
    toAccountName: 'Rent Payment',
    amount: 2000,
    currency: 'USD',
    frequency: 'monthly',
    nextRunDate: '2025-01-05T00:00:00Z',
    status: 'active',
    description: 'Monthly rent payment',
    createdAt: '2024-01-05T00:00:00Z',
  },
];

// Mock Bill Payments
export const mockBillPayments: BillPayment[] = [
  {
    id: 'bp_1',
    userId: 'u1',
    billerName: 'Pacific Gas & Electric',
    billerCategory: 'electricity',
    accountNumber: 'PG&E-12345',
    amount: 145.50,
    currency: 'USD',
    status: 'pending',
    dueDate: '2025-01-15T00:00:00Z',
    createdAt: '2024-12-15T00:00:00Z',
  },
  {
    id: 'bp_2',
    userId: 'u1',
    billerName: 'Comcast Internet',
    billerCategory: 'internet',
    accountNumber: 'Xfinity-67890',
    amount: 89.99,
    currency: 'USD',
    status: 'completed',
    dueDate: '2024-12-20T00:00:00Z',
    paidAt: '2024-12-18T10:30:00Z',
    createdAt: '2024-12-18T10:30:00Z',
  },
  {
    id: 'bp_3',
    userId: 'u1',
    billerName: 'T-Mobile',
    billerCategory: 'phone',
    accountNumber: 'TM-111222',
    amount: 75.00,
    currency: 'USD',
    status: 'completed',
    dueDate: '2024-12-25T00:00:00Z',
    paidAt: '2024-12-23T14:00:00Z',
    createdAt: '2024-12-23T14:00:00Z',
  },
];

export const mockUsers: User[] = [
  {
    id: 'u1',
    email: 'sarah.chen@email.com',
    phone: '+1-555-0101',
    fullName: 'Sarah Chen',
    avatar: 'https://i.pravatar.cc/150?u=sarah',
    kycStatus: 'verified',
    accountStatus: 'active',
    tier: 'premium',
    dailyLimit: 50000,
    weeklyLimit: 200000,
    monthlyLimit: 500000,
    balanceUsd: 26887.09,
    balanceEur: 22150.50,
    balanceGbp: 18920.75,
    balanceBtc: 0.45,
    btcPrice: 67540.20,
    address: '123 Market St, San Francisco, CA 94105',
    dateOfBirth: '1990-03-15',
    createdAt: '2024-01-10T08:00:00Z',
    updatedAt: '2024-12-15T14:30:00Z',
    lastActive: '2024-12-20T10:15:00Z',
    isOnline: true,
  },
  {
    id: 'u2',
    email: 'james.wilson@email.com',
    phone: '+1-555-0102',
    fullName: 'James Wilson',
    avatar: 'https://i.pravatar.cc/150?u=james',
    kycStatus: 'verified',
    accountStatus: 'active',
    tier: 'standard',
    dailyLimit: 25000,
    weeklyLimit: 100000,
    monthlyLimit: 250000,
    balanceUsd: 12450.75,
    balanceEur: 8500.00,
    balanceGbp: 6200.50,
    balanceBtc: 0.12,
    btcPrice: 67540.20,
    address: '456 Elm Ave, New York, NY 10001',
    dateOfBirth: '1985-07-22',
    createdAt: '2024-02-05T10:30:00Z',
    updatedAt: '2024-12-18T09:45:00Z',
    lastActive: '2024-12-19T16:20:00Z',
    isOnline: false,
  },
  {
    id: 'u3',
    email: 'maria.garcia@email.com',
    phone: '+1-555-0103',
    fullName: 'Maria Garcia',
    avatar: 'https://i.pravatar.cc/150?u=maria',
    kycStatus: 'pending',
    accountStatus: 'active',
    tier: 'basic',
    dailyLimit: 5000,
    weeklyLimit: 20000,
    monthlyLimit: 50000,
    balanceUsd: 3420.00,
    balanceEur: 2100.50,
    balanceGbp: 1500.00,
    balanceBtc: 0.03,
    btcPrice: 67540.20,
    createdAt: '2024-03-12T14:00:00Z',
    updatedAt: '2024-12-10T11:20:00Z',
    lastActive: '2024-12-15T13:00:00Z',
    isOnline: false,
  },
  {
    id: 'u4',
    email: 'alex.morgan@email.com',
    phone: '+1-555-0104',
    fullName: 'Alex Morgan',
    avatar: 'https://i.pravatar.cc/150?u=alex',
    kycStatus: 'verified',
    accountStatus: 'suspended',
    tier: 'standard',
    dailyLimit: 25000,
    weeklyLimit: 100000,
    monthlyLimit: 250000,
    balanceUsd: 8900.50,
    balanceEur: 4500.00,
    balanceGbp: 3200.75,
    balanceBtc: 0.08,
    btcPrice: 67540.20,
    address: '789 Pine Rd, Chicago, IL 60601',
    dateOfBirth: '1988-11-05',
    createdAt: '2024-04-20T09:15:00Z',
    updatedAt: '2024-12-01T08:30:00Z',
    lastActive: '2024-11-28T15:45:00Z',
    isOnline: false,
  },
  {
    id: 'u5',
    email: 'priya.patel@email.com',
    phone: '+1-555-0105',
    fullName: 'Priya Patel',
    avatar: 'https://i.pravatar.cc/150?u=priya',
    kycStatus: 'verified',
    accountStatus: 'active',
    tier: 'premium',
    dailyLimit: 50000,
    weeklyLimit: 200000,
    monthlyLimit: 500000,
    balanceUsd: 45200.30,
    balanceEur: 31200.00,
    balanceGbp: 25800.50,
    balanceBtc: 0.78,
    btcPrice: 67540.20,
    address: '321 Oak Blvd, Austin, TX 78701',
    dateOfBirth: '1992-01-30',
    createdAt: '2024-05-08T11:45:00Z',
    updatedAt: '2024-12-19T17:00:00Z',
    lastActive: '2024-12-20T09:30:00Z',
    isOnline: true,
  },
  {
    id: 'u6',
    email: 'robin.holesinsky@email.com',
    phone: '+1-555-0106',
    fullName: 'Robin Holesinsky',
    avatar: 'https://i.pravatar.cc/150?u=robin',
    kycStatus: 'verified',
    accountStatus: 'active',
    tier: 'premium',
    dailyLimit: 100000,
    weeklyLimit: 400000,
    monthlyLimit: 1000000,
    balanceUsd: 172101.87,
    balanceEur: 148500.00,
    balanceGbp: 125800.50,
    balanceBtc: 2.5,
    btcPrice: 67540.20,
    address: '555 Innovation Dr, San Jose, CA 95110',
    dateOfBirth: '1982-09-12',
    createdAt: '2023-08-15T07:00:00Z',
    updatedAt: '2024-12-20T08:00:00Z',
    lastActive: '2024-12-20T11:00:00Z',
    isOnline: true,
  },
  {
    id: 'u7',
    email: 'leo.martinez@email.com',
    phone: '+1-555-0107',
    fullName: 'Leo Martinez',
    avatar: 'https://i.pravatar.cc/150?u=leo',
    kycStatus: 'rejected',
    accountStatus: 'frozen',
    tier: 'basic',
    dailyLimit: 1000,
    weeklyLimit: 5000,
    monthlyLimit: 10000,
    balanceUsd: 150.00,
    balanceEur: 0,
    balanceGbp: 0,
    balanceBtc: 0,
    btcPrice: 67540.20,
    createdAt: '2024-06-01T16:00:00Z',
    updatedAt: '2024-11-15T10:00:00Z',
    lastActive: '2024-11-10T09:00:00Z',
    isOnline: false,
  },
  {
    id: 'u8',
    email: 'emma.thompson@email.com',
    phone: '+1-555-0108',
    fullName: 'Emma Thompson',
    avatar: 'https://i.pravatar.cc/150?u=emma',
    kycStatus: 'verified',
    accountStatus: 'active',
    tier: 'standard',
    dailyLimit: 25000,
    weeklyLimit: 100000,
    monthlyLimit: 250000,
    balanceUsd: 18500.00,
    balanceEur: 12000.50,
    balanceGbp: 9800.75,
    balanceBtc: 0.22,
    btcPrice: 67540.20,
    address: '888 Cedar Ln, Seattle, WA 98101',
    dateOfBirth: '1995-05-18',
    createdAt: '2024-07-20T12:30:00Z',
    updatedAt: '2024-12-17T14:00:00Z',
    lastActive: '2024-12-19T20:15:00Z',
    isOnline: false,
  },
];

export const mockTransactions: Transaction[] = [
  {
    id: 'txn_1',
    userId: 'u1',
    type: 'transfer',
    amount: -1050.00,
    currency: 'USD',
    status: 'completed',
    description: 'Transfer to James Wilson',
    recipientName: 'James Wilson',
    recipientAvatar: 'https://i.pravatar.cc/150?u=james',
    createdAt: '2024-12-20T10:00:00Z',
    updatedAt: '2024-12-20T10:00:00Z',
  },
  {
    id: 'txn_2',
    userId: 'u1',
    type: 'topup',
    amount: 2400.00,
    currency: 'USD',
    status: 'completed',
    description: 'Top up from Chase ****4521',
    createdAt: '2024-12-20T09:30:00Z',
    updatedAt: '2024-12-20T09:30:00Z',
  },
  {
    id: 'txn_3',
    userId: 'u1',
    type: 'transfer',
    amount: -5710.20,
    currency: 'USD',
    status: 'completed',
    description: 'Received from Eva Novak',
    recipientName: 'Eva Novak',
    recipientAvatar: 'https://i.pravatar.cc/150?u=eva',
    createdAt: '2024-12-19T16:45:00Z',
    updatedAt: '2024-12-19T16:45:00Z',
  },
  {
    id: 'txn_4',
    userId: 'u1',
    type: 'investment',
    amount: -714.00,
    currency: 'USD',
    status: 'completed',
    description: 'Binance Investment',
    createdAt: '2024-12-19T10:20:00Z',
    updatedAt: '2024-12-19T10:20:00Z',
  },
  {
    id: 'txn_5',
    userId: 'u1',
    type: 'transfer',
    amount: 428.00,
    currency: 'USD',
    status: 'completed',
    description: 'Received from Henrik Jansen',
    recipientName: 'Henrik Jansen',
    recipientAvatar: 'https://i.pravatar.cc/150?u=henrik',
    createdAt: '2024-12-18T14:30:00Z',
    updatedAt: '2024-12-18T14:30:00Z',
  },
  {
    id: 'txn_6',
    userId: 'u1',
    type: 'withdrawal',
    amount: -124.55,
    currency: 'USD',
    status: 'completed',
    description: 'Multiplex Purchase',
    createdAt: '2024-12-18T11:00:00Z',
    updatedAt: '2024-12-18T11:00:00Z',
  },
  {
    id: 'txn_7',
    userId: 'u1',
    type: 'withdrawal',
    amount: -328.96,
    currency: 'USD',
    status: 'completed',
    description: 'Nike Store Purchase',
    createdAt: '2024-12-17T15:20:00Z',
    updatedAt: '2024-12-17T15:20:00Z',
  },
  {
    id: 'txn_8',
    userId: 'u1',
    type: 'transfer',
    amount: 548.00,
    currency: 'USD',
    status: 'completed',
    description: 'Received from Matteo Ricci',
    recipientName: 'Matteo Ricci',
    recipientAvatar: 'https://i.pravatar.cc/150?u=matteo',
    createdAt: '2024-12-16T09:45:00Z',
    updatedAt: '2024-12-16T09:45:00Z',
  },
  {
    id: 'txn_9',
    userId: 'u1',
    type: 'withdrawal',
    amount: -847.20,
    currency: 'USD',
    status: 'completed',
    description: 'Megogo Subscription',
    createdAt: '2024-12-15T18:00:00Z',
    updatedAt: '2024-12-15T18:00:00Z',
  },
  {
    id: 'txn_10',
    userId: 'u1',
    type: 'transfer',
    amount: 147.00,
    currency: 'USD',
    status: 'pending',
    description: 'Received from Emilia Costa',
    recipientName: 'Emilia Costa',
    recipientAvatar: 'https://i.pravatar.cc/150?u=emilia',
    createdAt: '2024-12-15T12:00:00Z',
    updatedAt: '2024-12-15T12:00:00Z',
  },
  {
    id: 'txn_11',
    userId: 'u2',
    type: 'topup',
    amount: 5000.00,
    currency: 'USD',
    status: 'completed',
    description: 'Top up from Bank of America ****8823',
    createdAt: '2024-12-19T08:00:00Z',
    updatedAt: '2024-12-19T08:00:00Z',
  },
  {
    id: 'txn_12',
    userId: 'u2',
    type: 'transfer',
    amount: -1200.00,
    currency: 'USD',
    status: 'completed',
    description: 'Transfer to Sarah Chen',
    recipientName: 'Sarah Chen',
    recipientAvatar: 'https://i.pravatar.cc/150?u=sarah',
    createdAt: '2024-12-18T14:00:00Z',
    updatedAt: '2024-12-18T14:00:00Z',
  },
  {
    id: 'txn_13',
    userId: 'u6',
    type: 'credit',
    amount: 25000.00,
    currency: 'USD',
    status: 'completed',
    description: 'Admin credit: Quarterly bonus',
    adminId: 'admin_1',
    adminAction: 'add_funds',
    createdAt: '2024-12-20T08:00:00Z',
    updatedAt: '2024-12-20T08:00:00Z',
  },
  {
    id: 'txn_14',
    userId: 'u6',
    type: 'investment',
    amount: 58200.00,
    currency: 'USD',
    status: 'completed',
    description: 'Portfolio earnings this month',
    createdAt: '2024-12-19T00:00:00Z',
    updatedAt: '2024-12-19T00:00:00Z',
  },
];

export const mockChatRooms: ChatRoom[] = [
  {
    id: 'room_1',
    userId: 'u1',
    adminId: 'admin_2',
    status: 'active',
    userName: 'Sarah Chen',
    userAvatar: 'https://i.pravatar.cc/150?u=sarah',
    lastMessage: 'I need help with my recent transfer',
    lastMessageAt: '2024-12-20T10:30:00Z',
    unreadCount: 2,
    isUserOnline: true,
    createdAt: '2024-12-18T09:00:00Z',
    updatedAt: '2024-12-20T10:30:00Z',
  },
  {
    id: 'room_2',
    userId: 'u2',
    adminId: 'admin_2',
    status: 'active',
    userName: 'James Wilson',
    userAvatar: 'https://i.pravatar.cc/150?u=james',
    lastMessage: 'Thank you for your help!',
    lastMessageAt: '2024-12-19T16:00:00Z',
    unreadCount: 0,
    isUserOnline: false,
    createdAt: '2024-12-15T11:00:00Z',
    updatedAt: '2024-12-19T16:00:00Z',
  },
  {
    id: 'room_3',
    userId: 'u3',
    adminId: 'admin_2',
    status: 'flagged',
    userName: 'Maria Garcia',
    userAvatar: 'https://i.pravatar.cc/150?u=maria',
    lastMessage: 'My KYC has been pending for a week',
    lastMessageAt: '2024-12-20T08:15:00Z',
    unreadCount: 1,
    isUserOnline: false,
    createdAt: '2024-12-10T14:00:00Z',
    updatedAt: '2024-12-20T08:15:00Z',
  },
  {
    id: 'room_4',
    userId: 'u5',
    adminId: 'admin_2',
    status: 'resolved',
    userName: 'Priya Patel',
    userAvatar: 'https://i.pravatar.cc/150?u=priya',
    lastMessage: 'Issue resolved, thanks!',
    lastMessageAt: '2024-12-18T12:00:00Z',
    unreadCount: 0,
    isUserOnline: true,
    createdAt: '2024-12-12T10:00:00Z',
    updatedAt: '2024-12-18T12:00:00Z',
  },
];

export const mockChatMessages: ChatMessage[] = [
  {
    id: 'msg_1',
    roomId: 'room_1',
    senderId: 'u1',
    senderType: 'user',
    senderName: 'Sarah Chen',
    content: 'Hello, I have a question about my recent transfer',
    createdAt: '2024-12-20T09:00:00Z',
  },
  {
    id: 'msg_2',
    roomId: 'room_1',
    senderId: 'admin_2',
    senderType: 'admin',
    senderName: 'Support Team',
    content: 'Hi Sarah! I\'d be happy to help. Could you provide the transaction ID?',
    createdAt: '2024-12-20T09:05:00Z',
  },
  {
    id: 'msg_3',
    roomId: 'room_1',
    senderId: 'u1',
    senderType: 'user',
    senderName: 'Sarah Chen',
    content: 'It\'s txn_1 from earlier today for $1,050',
    createdAt: '2024-12-20T09:30:00Z',
  },
  {
    id: 'msg_4',
    roomId: 'room_1',
    senderId: 'u1',
    senderType: 'user',
    senderName: 'Sarah Chen',
    content: 'I need help with my recent transfer',
    createdAt: '2024-12-20T10:30:00Z',
  },
  {
    id: 'msg_5',
    roomId: 'room_3',
    senderId: 'u3',
    senderType: 'user',
    senderName: 'Maria Garcia',
    content: 'My KYC has been pending for a week now, any update?',
    createdAt: '2024-12-20T08:15:00Z',
  },
  {
    id: 'msg_6',
    roomId: 'room_3',
    senderId: 'admin_2',
    senderType: 'admin',
    senderName: 'Support Team',
    content: 'We are reviewing your documents. Should be resolved within 24 hours.',
    createdAt: '2024-12-20T08:30:00Z',
  },
];

export const mockAdminActions: AdminAction[] = [
  {
    id: 'act_1',
    adminId: 'admin_1',
    adminName: 'System Admin',
    actionType: 'add_funds',
    targetUserId: 'u6',
    targetUserName: 'Robin Holesinsky',
    amount: 25000,
    reason: 'Quarterly bonus',
    ipAddress: '192.168.1.100',
    createdAt: '2024-12-20T08:00:00Z',
  },
  {
    id: 'act_2',
    adminId: 'admin_1',
    adminName: 'System Admin',
    actionType: 'suspend_user',
    targetUserId: 'u4',
    targetUserName: 'Alex Morgan',
    reason: 'Suspicious activity detected',
    ipAddress: '192.168.1.100',
    createdAt: '2024-12-01T10:00:00Z',
  },
  {
    id: 'act_3',
    adminId: 'admin_3',
    adminName: 'Finance Admin',
    actionType: 'approve_kyc',
    targetUserId: 'u5',
    targetUserName: 'Priya Patel',
    ipAddress: '192.168.1.102',
    createdAt: '2024-12-10T14:30:00Z',
  },
  {
    id: 'act_4',
    adminId: 'admin_1',
    adminName: 'System Admin',
    actionType: 'update_user',
    targetUserId: 'u1',
    targetUserName: 'Sarah Chen',
    reason: 'Upgraded to Premium tier',
    ipAddress: '192.168.1.100',
    createdAt: '2024-12-15T09:00:00Z',
  },
  {
    id: 'act_5',
    adminId: 'admin_3',
    adminName: 'Finance Admin',
    actionType: 'flag_transaction',
    targetUserId: 'u7',
    targetUserName: 'Leo Martinez',
    amount: 5000,
    reason: 'Unusual large transfer pattern',
    ipAddress: '192.168.1.102',
    createdAt: '2024-11-10T16:00:00Z',
  },
];

export const mockKycDocuments: KycDocument[] = [
  {
    id: 'kyc_1',
    userId: 'u3',
    docType: 'id_card',
    url: 'https://placehold.co/400x250/222/fff?text=ID+Card',
    status: 'pending',
    uploadedAt: '2024-12-10T10:00:00Z',
    ocrData: { fullName: 'Maria Garcia', idNumber: 'ID-2024-003', expiryDate: '2029-05-15' },
  },
  {
    id: 'kyc_2',
    userId: 'u3',
    docType: 'selfie',
    url: 'https://placehold.co/400x400/222/fff?text=Selfie',
    status: 'pending',
    uploadedAt: '2024-12-10T10:05:00Z',
  },
  {
    id: 'kyc_3',
    userId: 'u3',
    docType: 'address_proof',
    url: 'https://placehold.co/400x250/222/fff?text=Utility+Bill',
    status: 'pending',
    uploadedAt: '2024-12-10T10:10:00Z',
  },
  {
    id: 'kyc_4',
    userId: 'u7',
    docType: 'id_card',
    url: 'https://placehold.co/400x250/222/fff?text=ID+Card',
    status: 'rejected',
    reviewedBy: 'admin_3',
    reviewedAt: '2024-11-15T10:00:00Z',
    rejectionReason: 'Document expired, please upload a valid ID',
    uploadedAt: '2024-11-01T14:00:00Z',
  },
];

export const mockAdminUsers: AdminUser[] = [
  {
    id: 'admin_1',
    userId: 'adm1',
    email: 'admin@orbitpay.com',
    fullName: 'System Admin',
    role: 'super_admin',
    permissions: ['all'],
    lastLogin: '2024-12-20T08:00:00Z',
    ipWhitelist: ['192.168.1.100'],
    isOnline: true,
    avatar: 'https://i.pravatar.cc/150?u=admin1',
  },
  {
    id: 'admin_2',
    userId: 'adm2',
    email: 'support@orbitpay.com',
    fullName: 'Support Team',
    role: 'support_admin',
    permissions: ['chat', 'view_users', 'view_transactions'],
    lastLogin: '2024-12-20T09:00:00Z',
    ipWhitelist: ['192.168.1.101'],
    isOnline: true,
    avatar: 'https://i.pravatar.cc/150?u=admin2',
  },
  {
    id: 'admin_3',
    userId: 'adm3',
    email: 'finance@orbitpay.com',
    fullName: 'Finance Admin',
    role: 'finance_admin',
    permissions: ['transactions', 'kyc', 'view_users'],
    lastLogin: '2024-12-19T17:00:00Z',
    ipWhitelist: ['192.168.1.102'],
    isOnline: false,
    avatar: 'https://i.pravatar.cc/150?u=admin3',
  },
];

export const mockNotifications: Notification[] = [
  {
    id: 'notif_1',
    userId: 'u1',
    title: 'Transfer Completed',
    message: 'Your transfer of $1,050 to James Wilson was successful',
    type: 'transaction',
    read: false,
    createdAt: '2024-12-20T10:00:00Z',
  },
  {
    id: 'notif_2',
    userId: 'u1',
    title: 'Top Up Successful',
    message: '$2,400 has been added to your account',
    type: 'transaction',
    read: false,
    createdAt: '2024-12-20T09:30:00Z',
  },
  {
    id: 'notif_3',
    userId: 'u1',
    title: 'Security Alert',
    message: 'New login detected from Chrome on Mac OS',
    type: 'security',
    read: true,
    createdAt: '2024-12-19T08:00:00Z',
  },
  {
    id: 'notif_4',
    userId: 'u1',
    title: 'Support Reply',
    message: 'Support team has replied to your inquiry',
    type: 'chat',
    read: false,
    createdAt: '2024-12-18T14:00:00Z',
  },
  {
    id: 'notif_5',
    userId: 'u1',
    title: 'KYC Verified',
    message: 'Your identity verification is complete',
    type: 'kyc',
    read: true,
    createdAt: '2024-12-15T10:00:00Z',
  },
];

export const seedData = () => {
  const { setUsers, setTransactions, setChatRooms, setChatMessages, setAdminActions, setKycDocuments, setAdminUsers, setNotifications, setBankAccounts, setCards, setLoans, setLoanPayments, setScheduledTransfers, setBillPayments } = useStore.getState();
  setUsers(mockUsers);
  setTransactions(mockTransactions);
  setChatRooms(mockChatRooms);
  setChatMessages(mockChatMessages);
  setAdminActions(mockAdminActions);
  setKycDocuments(mockKycDocuments);
  setAdminUsers(mockAdminUsers);
  setNotifications(mockNotifications);
  setBankAccounts(mockBankAccounts);
  setCards(mockCards);
  setLoans(mockLoans);
  setLoanPayments(mockLoanPayments);
  setScheduledTransfers(mockScheduledTransfers);
  setBillPayments(mockBillPayments);
};
